import type { TicketPriority, TicketStatus } from '../shared/index.js';

import type { BoardCard, DirectoryUser } from '../api/endpoints.js';

export const BOARD_VIEW_MODES = ['board', 'list', 'archived'] as const;
export type BoardViewMode = (typeof BOARD_VIEW_MODES)[number];

export const UNASSIGNED = 'unassigned';

export interface BoardFilters {
  readonly search: string;
  readonly assigneeIds: readonly string[];
  readonly departmentIds: readonly string[];
  readonly projectIds: readonly string[];
  readonly priorities: readonly TicketPriority[];
  readonly statuses: readonly TicketStatus[];
}

export const EMPTY_FILTERS: BoardFilters = {
  search: '',
  assigneeIds: [],
  departmentIds: [],
  projectIds: [],
  priorities: [],
  statuses: [],
};

export function activeFilterCount(filters: BoardFilters): number {
  let count = 0;

  if (filters.search.trim().length > 0) count += 1;
  if (filters.assigneeIds.length > 0) count += 1;
  if (filters.departmentIds.length > 0) count += 1;
  if (filters.projectIds.length > 0) count += 1;
  if (filters.priorities.length > 0) count += 1;
  if (filters.statuses.length > 0) count += 1;

  return count;
}

function personName(people: ReadonlyMap<string, DirectoryUser>, id: string | null): string {
  if (id === null) return '';
  const person = people.get(id);
  return person === undefined ? '' : `${person.firstName} ${person.lastName}`;
}

function haystackFor(ticket: BoardCard, people: ReadonlyMap<string, DirectoryUser>): string {
  return [
    `sbg#${String(ticket.number)}`,
    String(ticket.number),
    ticket.title,
    ticket.description ?? '',
    personName(people, ticket.requesterId),
    personName(people, ticket.assigneeId),
  ]
    .join(' ')
    .toLowerCase();
}

function passes<T extends string>(chosen: readonly T[], value: T | null): boolean {
  return chosen.length === 0 || (value !== null && chosen.includes(value));
}

export function applyFilters(
  tickets: readonly BoardCard[],
  filters: BoardFilters,
  people: ReadonlyMap<string, DirectoryUser>,
): readonly BoardCard[] {
  const needle = filters.search.trim().toLowerCase();

  return tickets.filter((ticket) => {
    if (!passes(filters.statuses, ticket.status)) return false;
    if (!passes(filters.priorities, ticket.priority)) return false;
    if (!passes(filters.departmentIds, ticket.departmentId)) return false;
    if (!passes(filters.projectIds, ticket.projectId)) return false;

    if (filters.assigneeIds.length > 0) {
      const matches = filters.assigneeIds.some((id) =>
        id === UNASSIGNED ? ticket.assigneeId === null : ticket.assigneeId === id,
      );

      if (!matches) return false;
    }

    if (needle.length > 0 && !haystackFor(ticket, people).includes(needle)) return false;

    return true;
  });
}
